import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import { addDays, addMonths, describe, isDay, monthEnd, monthStart, today, weekStart } from '../../dates';
import { useLanguage } from '../../contexts/language';
import { useRecords } from '../../hooks/records';

import { DateNav } from '../date-nav';
import { DayCard } from '../day-card';
import { LoadFailed } from '../load-failed';
import { MonthChart } from '../month-chart';
import { WeekStrip } from '../week-strip';

import styles from './style.module.css';

// The Diary: the patient's daily records. On a phone it is one day at a time —
// a full-width date line, the week strip, and that day's card (C23). On a
// desktop it is the month chart from the clinician's sheet, with the selected
// day's card under it so a record can be opened from there too.
//
// The selected day lives in the URL (?day=), so a reload or Back returns to
// the same day, and every part reads it from there rather than from state
// that could drift. A missing, malformed or future day means today.
//
// One request covers both views: the month of the selected day plus the
// whole week around it, which can reach into the months on either side.
export function Diary() {
  const { text, language } = useLanguage();
  const [ parameters, setParameters ] = useSearchParams();
  const [ now ] = useState(today);

  let day = parameters.get('day');

  if (isDay(day) === false || day > now) {
    day = now;
  }

  const week = weekStart(day);
  let from = monthStart(day);
  let to = monthEnd(day);

  if (week < from) {
    from = week;
  }

  if (addDays({ day: week, days: 6 }) > to) {
    to = addDays({ day: week, days: 6 });
  }

  const { days, error, retry } = useRecords({ from, to });

  const onSelect = useCallback(function(next) {
    setParameters({ day: next });
  }, [ setParameters ]);

  const onPreviousDay = useCallback(function() {
    onSelect(addDays({ day, days: -1 }));
  }, [ day, onSelect ]);

  const onNextDay = useCallback(function() {
    onSelect(addDays({ day, days: 1 }));
  }, [ day, onSelect ]);

  const onPreviousMonth = useCallback(function() {
    onSelect(addMonths({ day, months: -1 }));
  }, [ day, onSelect ]);

  // The month after can still be partly in the past: its days up to today
  // are reachable, and the selection stops at today.
  const onNextMonth = useCallback(function() {
    let next = addMonths({ day, months: 1 });

    if (next > now) {
      next = now;
    }

    onSelect(next);
  }, [ day, now, onSelect ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (days === undefined) {
    return null;
  }

  const selected = days.find(function({ date }) {
    return date === day;
  });
  const weekDays = days.filter(function({ date }) {
    return date >= week && date <= addDays({ day: week, days: 6 });
  });
  const monthDays = days.filter(function({ date }) {
    return date >= monthStart(day) && date <= monthEnd(day);
  });

  // The range is loaded, but a day it lacks is left out rather than read.
  let card = null;

  if (selected != null) {
    card = <DayCard day={ selected }/>;
  }

  let dayTitle = describe({ day, language });

  if (day === now) {
    dayTitle = `${ dayTitle } · ${ text.todayLower }`;
  }

  return (
    <section className={ styles.diary } aria-label={ text.diaryTitle }>
      <DateNav
        phone
        title={ dayTitle }
        previousLabel={ text.previousDay }
        nextLabel={ text.nextDay }
        onPrevious={ onPreviousDay }
        onNext={ onNextDay }
        canGoNext={ day < now }
      />

      <DateNav
        desktop
        title={ describe({ day, language, month: true }) }
        previousLabel={ text.previousMonth }
        nextLabel={ text.nextMonth }
        onPrevious={ onPreviousMonth }
        onNext={ onNextMonth }
        canGoNext={ monthEnd(day) < now }
      />

      <WeekStrip days={ weekDays } selected={ day } today={ now } onSelect={ onSelect }/>

      <MonthChart days={ monthDays } selected={ day } today={ now } onSelect={ onSelect }/>

      { card }
    </section>
  );
}
